import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useApolloClient, useSuspenseQuery } from '@apollo/client/react';
import { parseJwt } from '@realadvisor/auth-client';
import { getModuleIdsFromGrants } from '@realadvisor/constants';

import { gql } from '@graphql/__generated__';
import { type MeQuery } from '@graphql/__generated__/graphql';
import { watchSession } from '@graphql/session';
import {
  type Me,
  resolveAppData,
} from '@modules/_shared/providers/resolveAppData';
import { isNeitherAdminNorBroker } from '@modules/_shared/utils/access';
import { isAuthRoute } from '@shared/utils/authRoutes';
import {
  CRM_SESSION_COOKIE_REFRESH_INTERVAL_MS,
  syncCrmSessionCookie,
} from '@shared/utils/crm-session-cookie';

// Same-origin fan-out of "this user's `me` changed" to the other open tabs.
// Best-effort by design: `BroadcastChannel` is missing in jsdom and in a few
// hardened browser configurations, and a tab that cannot listen simply keeps
// today's behaviour (stale until reload) instead of breaking.
const ME_REFRESH_CHANNEL = 'crm:me-refresh';

const openMeRefreshChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  try {
    return new BroadcastChannel(ME_REFRESH_CHANNEL);
  } catch {
    return null;
  }
};

export const ME_QUERY = gql(/* GraphQL */ `
  query Me {
    get_me {
      user_ids_in_led_teams
      show_plans
      claimed_at
      me {
        id
        created_at
        is_admin
        is_broker
        onboarding_status
        has_done_onboarding
        title
        organisation {
          id
          name
          ...OrganisationAddress
        }
        language
        # GTM UserDetails params (used for GTM intercom setup)
        first_name
        last_name
        full_name
        has_used_trial_button
        has_free_crm_access
        is_unclaimed
        auto_assign_leads
        can_user_login_as
        can_edit_settings
        ...UserActiveModuleGrants
        show_in_agency_pages
        agent_slug
        tenant_id
        intercom_hash
        default_team {
          id
          name
          lng
          lat
          show_in_agency_pages
          route
          street_number
          postcode
          locality
          listings_agency_names
        }
        teams_users {
          team_id
        }
        emails {
          id
          email
          primary
          verified
        }
        tenant {
          slug
          id
          name
          country_code
          lng
          lat
          default_pipeline_id
          image {
            url
          }
        }
        subscriptions(order_by: [{ user_id: asc }, { status: asc }], limit: 1) {
          status
          customer
        }
        phone_numbers(
          order_by: [{ primary: desc }, { verified: desc }]
          limit: 1
        ) {
          id
          number
          primary
          verified
        }
        user_images(where: { is_primary: { _eq: true } }, limit: 1) {
          id
          image {
            id
            url
          }
        }
        teams: teams_users(where: { deactivated: { _eq: false } }) {
          is_leader
          team_id
          team {
            id
            name
            organisation_id
            listings_agency_names
          }
        }
        # Most recent grant only, like the preferences page read. A reconnect
        # inserts a second nylas-v3 row (the row id IS the grant id) and leaves
        # the old one invalid; an unordered read can surface the stale row and
        # keep the reconnect banner / connect-email prompt up after a
        # successful reconnect.
        nylas_account: logins(
          where: { provider: { _eq: "nylas-v3" } }
          limit: 1
          order_by: { created_at: desc }
        ) {
          id
          invalid
          self_username
        }
        email_signature
      }
    }
  }
`);

// Re-exported: `Me` is produced by `resolveAppData`, and half the CRM imports
// it from here.
export type { Me };

export const getActiveModuleIds = (
  me: Pick<NonNullable<Me>, 'module_grants'> | null | undefined,
): string[] => getModuleIdsFromGrants(me?.module_grants);

export const AppDataContext = createContext<{
  me: Me;
  noAccess?: { userId: string };
  // JWT `x-hasura-default-role`: the role every CRM request runs under; the
  // CRM sends no `X-Hasura-Role` header. Normal signed-in admins use
  // `tenant_admin` (see auth/refresh-token.ts).
  hasuraRole?: string | null;
  // Re-reads the app-wide `me` after something mutates it outside Apollo (the
  // Nylas connect / disconnect REST endpoints). Without it the whole app keeps
  // the boot-time snapshot until a full page reload. Rejects when the re-read
  // fails, so a caller that must converge can fall back to a reload.
  // Also tells the user's other open tabs to re-read: the mutation happened
  // once, but every tab gates its compose form on its own `me` snapshot.
  refetchMe: () => Promise<void>;
  needsOnboarding?: { userId: string };
} | null>(null);

export const AppDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const accessToken = localStorage.getItem('access_token');
  let decodedToken: any;
  let isNotAdminNorBroker = false;
  let isMultitenantRole = false;
  // decode access token
  if (accessToken) {
    decodedToken = parseJwt(accessToken);
    isMultitenantRole =
      decodedToken?.['https://hasura.io/jwt/claims'][
        'x-hasura-default-role'
      ] === 'multitenant';
    isNotAdminNorBroker = isNeitherAdminNorBroker(
      decodedToken.user?.isAdmin,
      decodedToken.user?.isBroker,
    );
  }

  const client = useApolloClient();
  // Named rather than inlined into `skip` because every re-read of `me` has to
  // honour it too. `BaseApp` renders its anonymous branch on `me == null`, so a
  // read that ignored this would drop a tab sitting on `/login` or mid
  // tenant-switch straight into the authenticated app.
  const meQuerySkipped =
    accessToken == null ||
    isNotAdminNorBroker ||
    isMultitenantRole ||
    // Route match, not a substring: /en/logins is a 404, not the login card,
    // and skipping the query there made a signed-in user look anonymous - the
    // catch-all then bounced them to /login, which bounces a live session back
    // to where it came from, and the two spun.
    isAuthRoute(location.pathname);
  const { data } = useSuspenseQuery(ME_QUERY, {
    skip: meQuerySkipped,
    fetchPolicy: 'no-cache',
  });

  // Deliberately NOT the suspense query's `refetch`: that one routes a failed
  // re-read back into render (errorPolicy defaults to `none`, so the queryRef
  // promise rejects and `use()` throws), and there is no error boundary above
  // this provider, so a transient network error would blank the whole CRM.
  // An imperative query keeps the failure inside the promise and leaves the
  // last good snapshot on screen; callers decide how to recover.
  // Keyed on the suspense query's own result so it can never outlive it: if that
  // query ever re-runs (a new access token remounts this tree), its fresh `data`
  // arrives with a new identity and the refresh is dropped rather than shadowing
  // it forever. One source of truth at any point in time.
  const [refreshed, setRefreshed] = useState<{
    base: MeQuery | undefined;
    value: MeQuery;
  } | null>(null);
  const refreshedData =
    refreshed != null && refreshed.base === data ? refreshed.value : null;

  const readMe = useCallback(async () => {
    // Nothing to refresh where the query itself does not run: on those routes
    // `data` is undefined, so a result would satisfy `refreshed.base === data`
    // and shadow the skip.
    if (meQuerySkipped) {
      return;
    }
    try {
      const result = await client.query({
        query: ME_QUERY,
        fetchPolicy: 'no-cache',
      });
      // Only ever refresh the user this tree is already rendering. The read
      // authenticates with whatever `access_token` localStorage holds *now*,
      // not the one this tab booted with, and `logoutUser` / `loginAsUser`
      // reload only the acting tab - so in a tab left behind by a logout and a
      // login as someone else, an unguarded assignment would swap `me` (id,
      // tenant, modules, hasuraRole) to the other account with no navigation,
      // on top of an Apollo cache still full of the first user's rows. A stale
      // tab must keep showing its stale user until something reloads it.
      const currentUserId = data?.get_me.me?.id ?? null;
      const nextUserId = result.data?.get_me.me?.id ?? null;
      if (result.data != null && nextUserId === currentUserId) {
        setRefreshed({ base: data, value: result.data });
      }
    } catch (error) {
      // Silence here would strand the caller (a connected mailbox still
      // rendering "Connect email"), so leave a trace and let it react.
      console.error('Failed to refresh the current user', error);
      throw error;
    }
  }, [client, data, meQuerySkipped]);

  // One channel object for the whole tab, used to both post and listen. That
  // is what keeps the acting tab from re-reading its own announcement:
  // BroadcastChannel delivers to every other object on the name, including
  // ones in the same tab, but never back to the object that posted.
  const [meRefreshChannel] = useState(openMeRefreshChannel);

  // Closed on unmount only. The listener effect below re-runs whenever `readMe`
  // changes, so closing in its teardown would kill a channel still in use.
  useEffect(() => () => meRefreshChannel?.close(), [meRefreshChannel]);

  const refetchMe = useCallback(async () => {
    // Announce before awaiting: the other tabs' re-read does not depend on
    // this one succeeding, and a throw here must not swallow the fan-out.
    // Guarded for the reverse too: `postMessage` on a closed channel throws
    // `InvalidStateError`, and letting that escape would skip the local
    // re-read - the one thing the caller actually waits on. Unreachable while
    // the channel is closed on unmount only, but the cost of being wrong here
    // is a caller falling back to a full page reload.
    try {
      // Carries the user it is about: the channel is origin-scoped, so tabs
      // left on a previous identity listen on it too, and a bare "refresh"
      // would have them spend a network read only to discard the result.
      meRefreshChannel?.postMessage({
        userId: data?.get_me.me?.id ?? null,
      });
    } catch {
      // The other tabs stay stale until they reload: the pre-PR behaviour.
    }
    await readMe();
  }, [meRefreshChannel, readMe, data]);

  // The events `refetchMe` announces all mean the same thing: this user's
  // mailbox was connected, reconnected or disconnected. Every other open tab
  // still holds the boot-time `me` - which is what gates the compose form -
  // so without this it keeps offering "Connect email" for a mailbox that is
  // in fact connected, until a full reload. Re-read only; a listening tab
  // never re-announces, so this cannot ping-pong.
  useEffect(() => {
    if (meRefreshChannel == null) {
      return;
    }
    const onMessage = (event: MessageEvent) => {
      // Ignore announcements about someone else. `readMe` refuses to apply a
      // mismatched result anyway, so this is only about not issuing the read;
      // an older announcement without the field is treated as "not mine".
      const announcedUserId =
        typeof event.data === 'object' && event.data != null
          ? (event.data as { userId?: string | null }).userId ?? null
          : null;
      if (announcedUserId !== (data?.get_me.me?.id ?? null)) {
        return;
      }
      readMe().catch(() => {});
    };
    meRefreshChannel.addEventListener('message', onMessage);
    return () => {
      meRefreshChannel.removeEventListener('message', onMessage);
    };
  }, [meRefreshChannel, readMe, data]);

  const meData = refreshedData ?? data;

  // Query schema in background
  const appData = useMemo(
    () =>
      resolveAppData({
        accessToken,
        decodedToken,
        meData,
        isMultitenantRole,
        isNotAdminNorBroker,
        meQuerySkipped,
      }),
    [
      meData,
      accessToken,
      decodedToken,
      isMultitenantRole,
      isNotAdminNorBroker,
      meQuerySkipped,
    ],
  );

  const value = useMemo(
    () => ({ ...appData, refetchMe }),
    [appData, refetchMe],
  );

  const hasCrmSession = appData.me != null;

  useEffect(() => {
    syncCrmSessionCookie(hasCrmSession);
    if (!hasCrmSession) {
      return;
    }

    const intervalId = window.setInterval(
      () => syncCrmSessionCookie(localStorage.getItem('access_token') != null),
      CRM_SESSION_COOKIE_REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(intervalId);
  }, [hasCrmSession]);

  // Cross-tab logout sends this tab to the login card, cross-tab login brings
  // it back.
  useEffect(watchSession, []);

  return (
    <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
  );
};

export const useAppData = () => {
  const context = useContext(AppDataContext);

  if (context == null) {
    throw Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};
